import { useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { postAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useUpdatesFeature from '@/hooks/useUpdatesFeature';
import { SystemMessageBlock, SystemMessageCategory, SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import useFundIdFromPath from '@/hooks/useFundIdFromPath';
import { OPEN_ANNOUNCEMENT_EVENT, OpenAnnouncementDetail, resolveAnnouncementUrl } from '@/components/Announcements/openAnnouncement';
import { AnnouncementImage } from '@/components/Announcements/AnnouncementImage';
import { LIVE_SYSTEM_MESSAGES_URL } from '@/pages/ManagementArea/SystemMessages/systemMessages';
import { iconForMessage } from './systemMessageIcons';
import styles from './UpdatesBell.module.scss';

/**
 * "עדכונים" - the announcements the office publishes, kept where a coordinator can come back to
 * them.
 *
 * ⚠️ The same messages already reach the user through SystemMessagesPopup, and that popup is not a
 * history: it shows a message once and then it is gone for good. A coordinator who closed it on
 * Sunday had no way to read it again on Monday, which is the fault this control exists to fix
 * (HAT-1099), and the missing way in from the header that HAT-1126 reported.
 *
 * ⚠️ Mounted in the site header, which sits ABOVE the routes and outside FundProvider - so nothing
 * here may touch the fund context. It does not need to: system messages belong to no fund.
 *
 * The design's three category chips (all / system / funds) are drawn only when the reader's list
 * holds more than one category (HAT-1489). They were taken out in HAT-1099 because "fund updates"
 * promised a recipient the data could not supply; announcements now have an audience, and the
 * office's fund-specific messages come next - until they do, a chip that always filters to nothing
 * would be noise, so it does not appear.
 */
export default function UpdatesBell() {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuthContext();
  const isFeatureOn = useUpdatesFeature();
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  /*
   * Same URL as the popup, so the two share one react-query cache entry instead of keeping separate
   * copies of the list. Each message carries this user's own isRead, kept on the server, so the
   * count says the same thing on a second computer.
   *
   * ⚠️ The flag gates the request too, not only the rendering. With the bell off there is nothing
   * for the response to feed, and a screen that keeps polling for a control nobody can see is the
   * kind of cost that hides for months.
   */
  const { data: liveMessages, refetch } = useGetAuthApi<SystemMessageDtoIn[]>(
    currentUser && isFeatureOn ? LIVE_SYSTEM_MESSAGES_URL : '',
  );

  const messages = useMemo(() => liveMessages ?? [], [liveMessages]);

  /*
   * Both marks go through one mutation: the only difference is the URL, and the server owns what
   * "all" means - the live set at the moment of the click, which the client may not agree with if
   * a message expired while the panel was open.
   */
  const markRead = useMutation({
    mutationFn: async (url: string) => {
      const headers = { Authorization: `Bearer ${currentUser?.token}` };
      await postAuth<void>(url, headers, {});
    },
    /*
     * ⚠️ This query's own refetch, NOT queryClient.invalidateQueries.
     *
     * useGetAuthApi builds its key as [url, someArrowFunction] - a function that is a new object on
     * every render. Invalidating by [url] alone did not reliably reach it, so a message was marked
     * read on the server and the panel went on showing it as unread until the page was reloaded by
     * hand. Asking the query to refetch itself removes the key-matching guesswork entirely, and the
     * popup reading the same cache entry gets the fresh list with it.
     */
    onSuccess: () => refetch(),
  });

  const count = useMemo(() => messages.filter((message) => !message.isRead).length, [messages]);

  /*
   * Newest first, and the order never changes while someone is reading - a row that jumps as it is
   * read is a row that gets lost. A feature's announcement is news from the day its flag opened, not
   * from the day its PR was written (HAT-1489).
   */
  const ordered = useMemo(
    () => [...messages].sort((a, b) => newsMoment(b).localeCompare(newsMoment(a))),
    [messages],
  );

  const categories = useMemo(() => Array.from(new Set(messages.map((message) => message.category))), [messages]);
  const [category, setCategory] = useState<SystemMessageCategory | 'all'>('all');
  const shown = category === 'all' ? ordered : ordered.filter((message) => message.category === category);

  const fundIdFromPath = useFundIdFromPath();
  const fundId = fundIdFromPath ?? currentUser?.fundCodes?.[0] ?? currentUser?.fundIds?.[0] ?? null;

  /*
   * «לפרטים» in a feature's bubble opens the bell with that announcement unfolded (HAT-1489). The
   * bubble has already marked it read.
   */
  useEffect(() => {
    const onOpen = (event: Event) => {
      const { id } = (event as CustomEvent<OpenAnnouncementDetail>).detail ?? {};
      if (typeof id !== 'number') return;
      setCategory('all');
      setExpandedId(id);
      setOpen(true);
    };
    window.addEventListener(OPEN_ANNOUNCEMENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ANNOUNCEMENT_EVENT, onOpen);
  }, []);

  const handleMessageClick = (message: SystemMessageDtoIn) => {
    setExpandedId(expandedId === message.id ? null : message.id);

    if (!message.isRead) {
      markRead.mutate(`system-messages/${message.id}/read`);
    }
  };

  /**
   * "היום, 09:40" for something from today, a full date otherwise. A coordinator checking whether
   * they already saw an update cares first about whether it is new, and only then about when.
   */
  const stampOf = (message: SystemMessageDtoIn): string => {
    const locale = i18n.language === 'en' ? 'en-GB' : 'he-IL';
    const at = new Date(newsMoment(message));
    const time = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (at.toDateString() === today.toDateString()) return t('updates.today', { time });
    if (at.toDateString() === yesterday.toDateString()) return t('updates.yesterday', { time });

    return `${at.toLocaleDateString(locale)}, ${time}`;
  };

  /*
   * Below the hooks, so the hook order stays identical whether or not this renders.
   *
   * Two separate reasons to draw nothing: the feature is not released here, or it is released and
   * there is simply no announcement live right now - an empty bell is chrome that does nothing, and
   * it comes back the moment a message is published.
   */
  if (!isFeatureOn || !currentUser || messages.length === 0) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={count > 0 ? styles.bellButtonUnread : styles.bellButton}
          aria-label={count > 0 ? t('updates.bellWithCount', { count }) : t('updates.bell')}
        >
          {count > 0 && <span className={styles.badge}>{count}</span>}
          <NotificationsNoneIcon sx={{ fontSize: 24 }} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.panel}
          align="end"
          sideOffset={8}
          collisionPadding={12}
          // A screenshot opened full size from here sits outside the panel; looking at it is not leaving.
          onInteractOutside={(event) => {
            if ((event.target as Element | null)?.closest?.('[data-announcement-image-viewer]')) event.preventDefault();
          }}
        >
          <div className={styles.panelTop}>
            <span className={`heading-h4-semibold ${styles.title}`}>{t('updates.title')}</span>
            {/* אין מה לסמן כשהכול נקרא - קישור שלא עושה כלום הוא רעש */}
            {count > 0 && (
              <button
                type="button"
                className={`body-sm-regular ${styles.markAll}`}
                onClick={() => markRead.mutate('system-messages/read-all')}
              >
                {t('updates.markAllRead')}
              </button>
            )}
          </div>

          {categories.length > 1 && (
            <div className={styles.filters} role="group" aria-label={t('updates.filters.label')}>
              {(['all', 'system', 'fund'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`body-sm-regular ${category === key ? styles.filterActive : styles.filter}`}
                  aria-pressed={category === key}
                  onClick={() => setCategory(key)}
                >
                  {t(`updates.filters.${key}`)}
                </button>
              ))}
            </div>
          )}

          <div className={styles.feed}>
            {shown.map((message) => {
              const isExpanded = expandedId === message.id;
              const hasMore = message.blocks.length > 0;

              return (
                <div key={message.id} className={message.isRead ? styles.note : styles.noteUnread}>
                  <span className={styles.avatar}>
                    <svg className={styles.avatarIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                      {iconForMessage(message)}
                    </svg>
                  </span>

                  <div className={styles.noteBody}>
                    {/* The text is the toggle; what unfolds below it may hold links, which a button cannot. */}
                    <button type="button" className={styles.noteToggle} aria-expanded={isExpanded} onClick={() => handleMessageClick(message)}>
                      {/* Unread is a dot by the title, not a word: the bold title already says it (HAT-1489 rework). */}
                      <span className={styles.titleLine}>
                        {!message.isRead && <span className={styles.unreadDot} aria-label={t('updates.new')} role="img" />}
                        <span className={`body-sm-semibold ${styles.noteTitle}`}>{message.title}</span>
                        {message.audience === 'office' && (
                          <span className={`body-xs-semibold ${styles.audience}`}>{t('announcements.audience.office')}</span>
                        )}
                      </span>
                      {message.body && (
                        <span className={`body-sm-regular ${isExpanded ? styles.noteTextOpen : styles.noteText}`}>
                          {message.body}
                        </span>
                      )}
                      {hasMore && !isExpanded && (
                        <span className={`body-sm-regular ${styles.more}`} aria-label={t('updates.more')}>{t('updates.more')} ›</span>
                      )}
                    </button>

                    {isExpanded && hasMore && (
                      <div className={styles.noteBlocks}>
                        {message.blocks.map((block, index) => (
                          <NoteBlock key={index} block={block} fundId={fundId} />
                        ))}
                      </div>
                    )}

                    <span className={`body-xs-sm-regular ${styles.stamp}`}>{stampOf(message)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** When a message became news: its flag's opening for a feature's announcement, otherwise its last edit. */
const newsMoment = (message: SystemMessageDtoIn) => message.publishedAt ?? message.updatedAt ?? message.createdAt;

/**
 * The rest of a message, unfolded in the panel: the popup's blocks at the panel's size (HAT-1489).
 * Until now the panel showed the lead paragraph only, so a message's list, picture and buttons were
 * reachable only in the sign-in popup - which shows a message once.
 */
const NoteBlock = ({ block, fundId }: { block: SystemMessageBlock; fundId: string | number | null }) => {
  switch (block.kind) {
    case 'image':
      return block.src ? <AnnouncementImage className={styles.noteImage} src={block.src} alt={block.text ?? ''} /> : null;
    case 'bullets':
      return (
        <ul className={`body-sm-regular ${styles.noteBullets}`}>
          {(block.items ?? []).map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      );
    case 'buttons':
      return (
        <div className={styles.noteButtons}>
          {(block.buttons ?? []).map((button, index) => {
            const href = resolveAnnouncementUrl(button.url, fundId);
            if (!href) return null;
            const isExternal = /^https?:\/\//i.test(href);
            return (
              <a
                key={index}
                href={href}
                className={`body-sm-semibold ${button.variant === 'primary' ? styles.noteButtonPrimary : styles.noteButton}`}
                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {button.label}
              </a>
            );
          })}
        </div>
      );
    case 'heading':
      return <span className={`body-sm-semibold ${styles.noteTitle}`}>{block.text}</span>;
    case 'callout':
    case 'paragraph':
    default:
      return <p className={`body-sm-regular ${block.kind === 'callout' ? styles.noteCallout : styles.noteParagraph}`}>{block.text}</p>;
  }
};
